import type { Language } from '@astral/shared';
import { DEFAULT_LANGUAGE } from '@astral/shared';
import { en } from './locales/en.js';
import { fa } from './locales/fa.js';
import { ru } from './locales/ru.js';
import type { Dictionary, TranslationKey, TranslationParams } from './types.js';

/** All dictionaries keyed by language code. */
export const dictionaries: Record<Language, Dictionary> = { en, fa, ru };

/** A bound translate function for a specific language. */
export type TranslateFn = (key: TranslationKey, params?: TranslationParams) => string;

/** Resolve a dot-path (e.g. "home.heroTitle") against a dictionary. */
function resolve(dict: Dictionary, key: string): string | undefined {
  const value = key
    .split('.')
    .reduce<unknown>((acc, part) => (acc && typeof acc === 'object' ? (acc as Record<string, unknown>)[part] : undefined), dict);
  return typeof value === 'string' ? value : undefined;
}

/** Replace {placeholders} in a template with the provided params. */
function interpolate(template: string, params?: TranslationParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
}

/**
 * Create a translator bound to `language`. Falls back to the default language,
 * then to the key itself, so the UI never renders `undefined`.
 */
export function createTranslator(language: Language): TranslateFn {
  const primary = dictionaries[language] ?? dictionaries[DEFAULT_LANGUAGE];
  const fallback = dictionaries[DEFAULT_LANGUAGE];

  return (key, params) => {
    const raw = resolve(primary, key) ?? resolve(fallback, key) ?? key;
    return interpolate(raw, params);
  };
}
